import { useEffect, useRef } from 'react';
import { DeviceCardProps } from '../../Components/CodeEditor/Devices/DeviceCardProps';
import { getMainProgramCode } from '../../Components/CodeEditor/utils/mainProgramEditor';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN, isUnsupportedDriveUnitPortRawValue } from '../../utils/driveUnitPortSelection';
import { isDriveUnitPortParameterName } from '../../utils/frameworkArgTypes';
import { applyDriveUnitPortStreamSync, portChangeSyncsDashboardStreams, skipNextDriveUnitPortStreamSyncRef } from './useConfigurePanelPortChange';
import { useControlInitialization, type UseControlInitializationOptions } from './useControlInitialization';

type PortInitOptions = Pick<
  UseControlInitializationOptions<number>,
  'min' | 'max' | 'parseValue' | 'isUnsupportedRawValue' | 'validateValue' | 'onValidationError'
>;

function isTrackedDriveUnitPort(port: number | undefined): port is number {
  return typeof port === 'number' && Number.isFinite(port) && port >= DRIVE_UNIT_PORT_MIN && port <= DRIVE_UNIT_PORT_MAX;
}

/**
 * Reads a drive-unit port from program code via `useControlInitialization` while the card stays
 * mounted above collapsible UI, and mirrors the value into `application.driveUnitPorts` so port
 * slot icons can grey out ports used elsewhere.
 *
 * When the port changes because the user edited the constructor in Monaco, also remaps dashboard
 * stream keys and `set_request_list` (same behavior as clicking a port in `DriveUnitPortControl`).
 */
export function useDriveUnitPortRegistry(
  props: DeviceCardProps,
  portParameterName: string,
  controlInitOptions: Partial<PortInitOptions> = {}
): {
  port: number | undefined;
  setPort: (value: number) => void;
  portControlValue: number | undefined;
  setPortControlValue: (value: number) => void;
  unsupportedRawValue?: string;
} {
  const dispatch = useAppDispatch();
  const readOnly = useAppSelector((state) => state.project?.loadedProject?.readOnly);
  const projectId = useAppSelector((state) => state.project?.loadedProject?.id);
  const dashboardLayout = useAppSelector((state) => state.project?.loadedProject?.dashboardLayout);
  const projectCode = useAppSelector((state) => state.project?.loadedProject?.program?.code);

  const portProps: DeviceCardProps = { ...props, parameterName: portParameterName };
  const {
    value: port,
    setValue: setPort,
    unsupportedRawValue,
  } = useControlInitialization<number>(portProps, {
    parseValue: (v) => Number.parseInt(v, 10),
    isUnsupportedRawValue: isUnsupportedDriveUnitPortRawValue,
    ...controlInitOptions,
  });
  const electronicKey = `${props.id}:${portParameterName}`;
  const registryRev = props.driveUnitPortRegistryRev ?? 0;
  const effectivePort = unsupportedRawValue ? undefined : port;
  const prevPortRef = useRef<number | undefined>(effectivePort);

  useEffect(() => {
    if (!isTrackedDriveUnitPort(effectivePort)) {
      dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: electronicKey }));
      return;
    }
    dispatch(applicationSlice.actions.onUpdateDriveUnitPorts({ driveUnitPort: effectivePort, electronic: electronicKey }));
  }, [dispatch, effectivePort, electronicKey, registryRev]);

  useEffect(() => {
    const oldPort = prevPortRef.current;
    prevPortRef.current = effectivePort;

    if (skipNextDriveUnitPortStreamSyncRef.current) {
      skipNextDriveUnitPortStreamSyncRef.current = false;
      return;
    }

    if (!portChangeSyncsDashboardStreams(portParameterName)) return;
    if (!isDriveUnitPortParameterName(portParameterName)) return;
    if (!isTrackedDriveUnitPort(effectivePort) || !isTrackedDriveUnitPort(oldPort)) return;
    if (oldPort === effectivePort) return;

    const programText = getMainProgramCode(props.editor, projectCode ?? '');
    applyDriveUnitPortStreamSync(dispatch, {
      readOnly,
      projectId,
      dashboardLayout,
      programCode: programText,
      originalProgramCode: programText,
      electronic: electronicKey,
      parameterName: portParameterName,
      oldPort,
      newPort: effectivePort,
    });
  }, [dispatch, readOnly, projectId, dashboardLayout, projectCode, effectivePort, portParameterName, electronicKey, props.editor]);

  return { port, setPort, portControlValue: effectivePort, setPortControlValue: setPort, unsupportedRawValue };
}
